import * as React from "react";
import MenuItem from "material-ui/MenuItem";
import IconButton from "material-ui/IconButton";
import MoreHorizIcon from "material-ui/svg-icons/navigation/more-horiz";
import PriorityHigh from "material-ui/svg-icons/notification/priority-high";
import Menu from "@material-ui/core/Menu";
import { formatMessage } from "lib/globalize-util";
import classNames from "classnames";
import { toDataTest } from "helpers/test";
import { DATATEST } from "constants/integration";
import { DEFAULT_VIEW_NAME } from "../product-collection-grid-view/tool-panels";

export interface ISavedView {
    id: string;
    name: string;
}

export const MERCHANT_SAVED_VIEW_VISUAL_DECORATOR = "*";

interface IProps {
    view: ISavedView;
    currentViewId?: string;
    onLoad: (viewName: string, viewId: string, viewIndex: number) => void;
    schemaIdWithRemovedFields?: string;
    onUpdate?: (viewId: string, viewName: string, viewIndex: number) => void;
    onMerchantViewUpdate?: (viewId: string, viewName: string, viewIndex: number) => void;
    onMerchantViewDelete?: (viewId: string, viewName: string, viewIndex: number) => void;
    onPromote?: (viewId: string, viewName: string, viewIndex: number) => void;
    onDelete?: (viewId: string, viewIndex: number, savedView: ISavedView) => void;
    onNewView?: () => void;
    onReset?: (viewId: string, viewName: string, viewIndex: number) => void;
    viewIndex: number;
    onOpenRemovedFieldModal?: (e) => void;
    editable: boolean;
    isActive: boolean;
    isMerchantSavedView: boolean;
}

export const SavedView: React.FunctionComponent<IProps> = ({
    view,
    onLoad,
    currentViewId,
    schemaIdWithRemovedFields,
    onOpenRemovedFieldModal,
    onUpdate,
    onMerchantViewUpdate,
    onMerchantViewDelete,
    onPromote,
    onDelete,
    viewIndex,
    onNewView,
    editable,
    onReset,
    isActive,
    isMerchantSavedView,
}) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleOpenMenu: React.EventHandler<React.MouseEvent<HTMLElement>> = React.useCallback((e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    }, []);

    const handleCloseMenu: React.EventHandler<React.MouseEvent<HTMLElement>> = React.useCallback((e) => {
        e.stopPropagation();
        setAnchorEl(null);
    }, []);

    const handleUpdate = React.useCallback(() => {
        onUpdate(view.id, view.name, viewIndex);
        setAnchorEl(null);
    }, [onUpdate, view, viewIndex]);

    const handleMerchantUpdate = React.useCallback(() => {
        onMerchantViewUpdate(view.id, view.name, viewIndex);
        setAnchorEl(null);
    }, [onMerchantViewUpdate, view, viewIndex]);

    const handleMerchantDelete = React.useCallback(() => {
        onMerchantViewDelete(view.id, view.name, viewIndex);
        setAnchorEl(null);
    }, [onMerchantViewDelete, view, viewIndex]);

    const handlePromote = React.useCallback(() => {
        if (onPromote) {
            onPromote(view.id, view.name, viewIndex);
            setAnchorEl(null);
        }
    }, [onPromote, view, viewIndex]);

    const handleDelete = React.useCallback(
        (e) => {
            e.stopPropagation();

            onDelete(view.id, viewIndex, view);
            setAnchorEl(null);
        },
        [onDelete, view, viewIndex]
    );

    const handleLoad = React.useCallback(() => {
        onLoad(view.name, view.id, viewIndex);
    }, [onLoad, view, viewIndex]);

    const handleReset = React.useCallback(() => {
        onReset(view.id, view.name, viewIndex);
        setAnchorEl(null);
    }, [onReset, view, viewIndex]);

    const handleNewView = React.useCallback(
        (e) => {
            e.stopPropagation();
            onNewView();
            setAnchorEl(null);
        },
        [onNewView]
    );

    return (
        <div role="listitem">
            <MenuItem
                data-view-name={view.name}
                data-view-id={view.id}
                data-test={`view-${toDataTest(view.name)}`}
                onClick={handleLoad}
                className={classNames("view", { selectedView: isActive })}
            >
                <div className="viewEntry">
                    <div className="viewName">{`${isMerchantSavedView ? MERCHANT_SAVED_VIEW_VISUAL_DECORATOR : ""}${
                        view.name
                    }`}</div>
                    {editable && (
                        <div className="viewButtons">
                            <IconButton
                                className={classNames("notifyIconButton", {
                                    notifyIconButtonDisplay:
                                        view.id === schemaIdWithRemovedFields &&
                                        view.name !== DEFAULT_VIEW_NAME &&
                                        currentViewId === view.id,
                                })}
                                onClick={onOpenRemovedFieldModal}
                                tooltip={formatMessage("View has been updated")}
                                tooltipPosition="bottom-left"
                            >
                                <PriorityHigh />
                            </IconButton>
                            {(!isMerchantSavedView ||
                                (isMerchantSavedView && (onMerchantViewUpdate || onMerchantViewDelete))) && (
                                <IconButton
                                    data-test={`${DATATEST.ASSORTMENTS.VIEWS_ACTION_MENU}-${toDataTest(view.name)}`}
                                    className="iconButton"
                                    data-view-name={view.name}
                                    onClick={handleOpenMenu}
                                >
                                    <MoreHorizIcon />
                                </IconButton>
                            )}
                        </div>
                    )}
                </div>
            </MenuItem>
            {anchorEl && isMerchantSavedView ? (
                <Menu
                    anchorEl={anchorEl}
                    keepMounted={true}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    className="menu"
                >
                    <MenuItem
                        className="viewMenuItem"
                        onClick={handleMerchantUpdate}
                        disabled={currentViewId !== view.id}
                        data-test={`${DATATEST.ASSORTMENTS.MERCHANT_SAVED_VIEWS_UPDATE}-${toDataTest(view.name)}`}
                    >
                        {formatMessage("Update")}
                    </MenuItem>
                    {onMerchantViewDelete && (
                        <MenuItem
                            className="viewMenuItem"
                            onClick={handleMerchantDelete}
                            disabled={currentViewId !== view.id}
                            data-test={`${DATATEST.ASSORTMENTS.MERCHANT_SAVED_VIEWS_DELETE}-${toDataTest(view.name)}`}
                        >
                            {formatMessage("Delete")}
                        </MenuItem>
                    )}
                </Menu>
            ) : (
                <Menu
                    anchorEl={anchorEl}
                    keepMounted={true}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    className="menu"
                >
                    {onPromote && (
                        <MenuItem
                            className="viewMenuItem"
                            onClick={handlePromote}
                            data-test={`${DATATEST.ASSORTMENTS.SAVED_VIEWS_PROMOTE}-${toDataTest(view.name)}`}
                        >
                            {formatMessage("Promote")}
                        </MenuItem>
                    )}
                    <MenuItem
                        className="viewMenuItem"
                        onClick={handleUpdate}
                        disabled={currentViewId !== view.id}
                        data-test={`${DATATEST.ASSORTMENTS.SAVED_VIEWS_UPDATE}-${toDataTest(view.name)}`}
                    >
                        {formatMessage("Update")}
                    </MenuItem>
                    <MenuItem
                        className="viewMenuItem"
                        onClick={handleNewView}
                        data-test={`${DATATEST.ASSORTMENTS.SAVED_VIEWS_SAVE_AS}-${toDataTest(view.name)}`}
                    >
                        {formatMessage("Save As")}
                    </MenuItem>
                    <MenuItem
                        className="viewMenuItem"
                        onClick={handleReset}
                        data-test={`${DATATEST.ASSORTMENTS.SAVED_VIEWS_RESET}-${toDataTest(view.name)}`}
                    >
                        {formatMessage("Reset")}
                    </MenuItem>
                    {view.name !== DEFAULT_VIEW_NAME && (
                        <MenuItem
                            className="viewMenuItem"
                            onClick={handleDelete}
                            data-test={`${DATATEST.ASSORTMENTS.SAVED_VIEWS_DELETE}-${toDataTest(view.name)}`}
                        >
                            {formatMessage("Delete")}
                        </MenuItem>
                    )}
                </Menu>
            )}
        </div>
    );
};
